
import { defineComponent, ref, watch, nextTick, onMounted } from 'vue'
import type { RebalancingRecord, ColorMode } from '@/types/arkOne'
import { arkOneApi } from '@/api'
import { transformRebalancingHistory } from '@/utils/arkOneTransform'
import { zh_tran } from '@/utils/lang.js'
import {
  formatDate as formatDateUtil,
  formatPrice,
  formatPercentage,
  getValueColor,
  getDirectionColor as getDirectionColorUtil
} from '@/utils/arkOneUtils'
import Nodata from '@/components/page/nodata.vue'
import StockCodeDisplay from './StockCodeDisplay.vue'

const __default__ = defineComponent({
  name: 'RebalancingHistoryTable',
  components: {
    Nodata,
    StockCodeDisplay
  },
  props: {
    selectedDate: {
      type: String,
      default: null
    },
    colorMode: {
      type: String as () => ColorMode,
      default: 'red-rise-green-fall'
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    // 主题：'dark' | 'light'
    theme: {
      type: String,
      default: 'dark'
    },
    // 语言支持：'zh-Hans' 简体中文，'zh-Hant' 繁体中文
    language: {
      type: String,
      default: 'zh-Hans'
    },
    // 港股延时
    hkDelay: {
      type: Boolean,
      default: false
    },
    // 美股延时
    usDelay: {
      type: Boolean,
      default: false
    }
  },
  emits: ['rowDoubleClick'],
  setup(props, { emit }) {
    const records = ref<RebalancingRecord[]>([])
    const loading = ref(true)
    const error = ref<string | null>(null)
    const rowRefs = ref<Map<string, HTMLElement>>(new Map())
    const isTraditional = () => props.language === 'zh-Hant'

    // 设置行引用（只保留每个日期的第一条）
    const setRowRef = (el: any, date: string) => {
      if (el && !rowRefs.value.has(date)) {
        rowRefs.value.set(date, el)
      }
    }

    // 获取调仓历史数据（支持分页）
    const fetchData = async () => {
      loading.value = true
      error.value = null
      
      try {
        // 第一次请求，获取总数
        const firstResponse = await arkOneApi.getPerformanceData({
          params: { page: 1, page_size: 100 },
          headers: {}
        })
        
        console.log('调仓历史第一页响应:', firstResponse)
        
        // 检查响应结构 - 注意：响应结构是 { status, code, data }
        if (!firstResponse) {
          throw new Error('响应数据为空')
        }
        
        // 检查 code 字段（在顶层，不是 data 里面）
        if (firstResponse.code !== 0) {
          throw new Error(firstResponse.message || '获取数据失败')
        }
        
        const firstPageData = firstResponse.data
        
        if (!firstPageData || !firstPageData.list) {
          throw new Error('数据结构异常')
        }
        
        // 避免使用展开语法，兼容旧版 tslib/vetur 校验
        let allRecords = Array.isArray(firstPageData.list) ? firstPageData.list.slice() : []
        
        // 计算总页数
        const total = parseInt(firstPageData.total || '0')
        const pageSize = 100
        const totalPages = Math.ceil(total / pageSize)
        
        console.log(`调仓历史总记录数: ${total}, 总页数: ${totalPages}`)
        
        // 如果有多页，继续请求剩余页面
        if (totalPages > 1) {
          const remainingRequests: Promise<any>[] = []
          for (let page = 2; page <= totalPages; page++) {
            remainingRequests.push(
              arkOneApi.getPerformanceData({
                params: { page, page_size: pageSize },
                headers: {}
              })
            )
          }
          
          // 并发请求所有剩余页面
          const remainingResponses = await Promise.all(remainingRequests)
          
          // 合并所有页面的数据
          remainingResponses.forEach((response, index) => {
            if (response && response.code === 0 && response.data && response.data.list) {
              allRecords = allRecords.concat(response.data.list)
              console.log(`成功加载第 ${index + 2} 页，记录数: ${response.data.list.length}`)
            }
          })
        }
        
        // 转换所有调仓历史记录
        records.value = transformRebalancingHistory({ list: allRecords })
        rowRefs.value.clear()
        
        console.log(`成功加载 ${allRecords.length} 条调仓历史记录`)
      } catch (err: any) {
        console.error('获取调仓历史异常:', err)
        error.value = err.message || '加载失败，请重试'
      } finally {
        loading.value = false
      }

      // 数据/状态更新发生在全局转换之后，这里补一次 DOM 转换避免遗漏
      if (isTraditional()) {
        await nextTick()
        zh_tran('t')
      }
    }

    // 格式化日期为 YY/MM/DD
    const formatDate = (date: string): string => {
      return formatDateUtil(date, 'YY/MM/DD')
    }

    // 格式化仓位变化显示为 "pre% → current%"（不带+/-符号）
    const formatPositionChange = (record: RebalancingRecord): string => {
      const preRatio = `${record.preStockRatio.toFixed(2)}%`
      const currentRatio = `${record.stockRatio.toFixed(2)}%`
      return `${preRatio} → ${currentRatio}`
    }

    // 获取仓位变化颜色：仓位无变化时为默认色，买入为红色，卖出为绿色
    const getChangeColor = (record: RebalancingRecord): string => {
      if (record.preStockRatio === record.stockRatio) return ''
      return record.direction === '买入' ? '#ff4d4f' : '#52c41a'
    }

    // 获取方向颜色：仓位无变化时为默认色，买入为红色，卖出为绿色
    const getDirectionColor = (record: RebalancingRecord): string => {
      if (record.preStockRatio === record.stockRatio) return ''
      return record.direction === '买入' ? '#ff4d4f' : '#52c41a'
    }

    // 处理行双击
    const handleRowDoubleClick = (record: RebalancingRecord) => {
      emit('rowDoubleClick', record)
    }

    // 监听选中日期变化，滚动到对应行
    watch(() => props.selectedDate, (newDate) => {
      if (newDate) {
        nextTick(() => {
          const row = rowRefs.value.get(newDate)
          if (row) {
            row.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }
        })
      }
    })

    // 组件挂载时获取数据
    onMounted(() => {
      fetchData()
    })

    // loading/error/records 的 DOM 都可能是异步插入的，统一补转换
    watch(
      () => [loading.value, error.value, records.value.length, props.showTitle],
      async () => {
        if (isTraditional()) {
          await nextTick()
          zh_tran('t')
        }
      }
    )

    return {
      records,
      loading,
      error,
      setRowRef,
      fetchData,
      formatDate,
      formatPrice,
      formatPercentage,
      formatPositionChange,
      getChangeColor,
      getDirectionColor,
      handleRowDoubleClick
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "a30d0134": (_ctx.theme === "light" ? "#ffffff" : "#111424"),
  "1593caea": (_ctx.theme === "light" ? "#000000" : "#ffffff"),
  "146c952a": (_ctx.theme === "light" ? "#333333" : "#ffffff"),
  "46da2081": (_ctx.theme === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)"),
  "1b2f35fa": (_ctx.theme === "light" ? "#f0f0f0" : "#0a0a0a"),
  "5a8b8d2b": (_ctx.theme === "light" ? "#666666" : "#999999"),
  "60fb2953": (_ctx.theme === "light" ? "#f5f7fa" : "#0f1426"),
  "02601c28": (_ctx.theme === "light" ? "#e4e7ed" : "#1f1f1f"),
  "4e1cb25a": (_ctx.theme === "light" ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.35)"),
  "9c1257e6": (_ctx.theme === "light" ? "#f5f5f5" : "rgba(255, 255, 255, 0.05)"),
  "1b02ce33": (_ctx.theme === "light" ? "rgba(24, 144, 255, 0.15)" : "rgba(24, 144, 255, 0.2)"),
  "119a422b": (_ctx.theme === "light" ? "#e4e7ed" : "#333333"),
  "91bb85aa": (_ctx.theme === "light" ? "#999999" : "#666666")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__